import React, { useState, useRef, useEffect } from 'react';
import { FaHeadphones, FaPlay, FaPause, FaRedo, FaTimes, FaFont } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import About from './components/About';
import Skills from './components/Skills';
import Projects from './components/Projects';
import Contact from './components/Contact';
import Footer from './components/Footer';

const Portfolio = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMusicMenu, setShowMusicMenu] = useState(false);
  const [isCustomFont, setIsCustomFont] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    AOS.init({
      duration: 500,
      once: true,
      offset: 100,
      easing: 'ease-out-cubic',
      disable: window.innerWidth < 768
    });

    audioRef.current = new Audio('/audio/site-music.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = 0.3; // Set volume to 30% 

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isCustomFont) {
      document.body.classList.add('font-custom');
    } else {
      document.body.classList.remove('font-custom');
    }
  }, [isCustomFont]);

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(error => {
          console.log('Audio play failed:', error);
        });
        setIsPlaying(true);
      }
    }
  };

  const restartMusic = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      if (!isPlaying) {
        audioRef.current.play().catch(error => {
          console.log('Audio play failed:', error);
        });
        setIsPlaying(true);
      }
    }
    setShowMusicMenu(false);
  };

  const handleMainMusicClick = () => {
    setShowMusicMenu(!showMusicMenu);
  };

  const handleBackgroundClick = () => {
    if (showMusicMenu) {
      setShowMusicMenu(false);
    }
  };

  const handleMusicAction = (action) => {
    if (action === 'toggle') {
      toggleMusic();
    } else if (action === 'restart') {
      restartMusic();
    } else if (action === 'close') {
      setShowMusicMenu(false);
    }
  };
  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-gray-200 relative transition-colors duration-300">
      <Navigation />
      <Hero />
      <About />
      <Skills />
      <Projects />
      <Contact />
      <Footer />
      
      {/* Background Blur Overlay */}
      {showMusicMenu && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm z-[9998]"
          onClick={handleBackgroundClick}
        />
      )}
      
      {/* Floating Action Buttons */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] flex flex-col sm:flex-row gap-3 sm:gap-4 items-center">
        
        {/* Font Toggle Button */}
        <button
          onClick={() => setIsCustomFont(!isCustomFont)}
          className={`w-12 h-12 sm:w-14 sm:h-14 bg-black dark:bg-white text-white dark:text-black rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center group active:scale-95 sm:hover:scale-110 relative border-[3px] ${isCustomFont ? 'border-gray-400 dark:border-gray-400' : 'border-transparent'} ${showMusicMenu ? 'opacity-0 scale-50 pointer-events-none' : 'opacity-100 scale-100'}`}
          aria-label="Toggle Custom Font"
          title="Toggle Custom Font"
          style={{ touchAction: 'manipulation' }}
        >
          <FaFont className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-300" />
        </button>

        {/* Floating Music Player Wrapper */}
        <div className="relative">
          {/* Main Music Button */}
          <button
            onClick={handleMainMusicClick}
            className="w-12 h-12 sm:w-14 sm:h-14 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center group active:scale-95 sm:hover:scale-110 relative"
            aria-label="Music Player Menu"
            title="Music Player"
            style={{ touchAction: 'manipulation' }}
          >
            <FaHeadphones className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-300" />
            
            {/* Animated Sound Waves (when playing) */}
            {isPlaying && (
              <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 flex space-x-0.5">
                <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-white dark:bg-black rounded-full animate-pulse"></div>
                <div className="w-0.5 h-1 sm:w-1 sm:h-1.5 bg-white dark:bg-black rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-white dark:bg-black rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            )}
          </button>

          {/* Popup Menu - Quarter Circle Arc */}
          {showMusicMenu && (
            <div className="absolute">
              {/* Play/Pause Button - 0 degrees (directly left) */}
              <button
                onClick={() => handleMusicAction('toggle')}
                className="absolute w-10 h-10 sm:w-12 sm:h-12 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center group active:scale-95 sm:hover:scale-110 animate-arc-0"
                aria-label={isPlaying ? 'Pause music' : 'Play music'}
                title={isPlaying ? 'Pause Music' : 'Play Music'}
                style={{ 
                  touchAction: 'manipulation',
                  right: window.innerWidth >= 640 ? '20px' : '18px',
                  bottom: '0px'
                }}
              >
                {isPlaying ? (
                  <FaPause className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" />
                ) : (
                  <FaPlay className="w-4 h-4 sm:w-5 sm:h-5 ml-0.5 group-hover:scale-110 transition-transform duration-300" />
                )}
              </button>

              {/* Restart Button - 45 degrees (diagonal up-left) */}
              <button
                onClick={() => handleMusicAction('restart')}
                className="absolute w-10 h-10 sm:w-12 sm:h-12 bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center group active:scale-95 sm:hover:scale-110 animate-arc-45"
                aria-label="Restart music"
                title="Restart Music"
                style={{ 
                  touchAction: 'manipulation',
                  right: window.innerWidth >= 640 ? '0px' : '-1px',
                  bottom: window.innerWidth >= 640 ? '52px' : '46px'
                }}
              >
                <FaRedo className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" />
              </button>

              {/* Close Button - 90 degrees (directly above) */}
              <button
                onClick={() => handleMusicAction('close')}
                className="absolute w-10 h-10 sm:w-12 sm:h-12 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center group active:scale-95 sm:hover:scale-110 animate-arc-90"
                aria-label="Close menu"
                title="Close Menu"
                style={{ 
                  touchAction: 'manipulation',
                  right: window.innerWidth >= 640 ? '-50px' : '-44px',
                  bottom: window.innerWidth >= 640 ? '80px' : '72px'
                }}
              >
                <FaTimes className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Portfolio;